import { Head } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { creatorListingsPath, serviceCreatorStagePath } from "@travily/routes";
import type ListingsCreatorSellingCountry from "@travily/types/pages/ListingsCreatorSellingCountry";
import { useCreatorTopBarActions } from "@/lib/wizard/use-creator-top-bar-actions";
import { useSellingCountryStage } from "@/lib/wizard/use-selling-country-stage";
import { ServiceSellingCountryStepView } from "./show.view";

type PageProps = ListingsCreatorSellingCountry;

// Asked once for a paid seller who has not told us where they get paid; sets the
// account country -> currency, framed as banking/payout, never taxes. The confirm-
// before-reprice flow lives in useSellingCountryStage (shared with products).
const ServiceSellingCountryStep = ({
  listing_id,
  stage_index,
  stage_order,
  country,
  current_currency,
  repriceable_count,
  supported_countries,
}: PageProps) => {
  const {
    form,
    fieldError,
    currentStage,
    totalStages,
    onBack,
    primaryPending,
    confirmOpen,
    handleContinue,
    handleConfirm,
    handleConfirmOpenChange,
  } = useSellingCountryStage({
    stageUrl: (stage) => serviceCreatorStagePath(listing_id, stage),
    stageIndex: stage_index,
    stageOrder: stage_order,
    country,
    currentCurrency: current_currency,
    repriceableCount: repriceable_count,
    supportedCountries: supported_countries,
  });

  const countryError = fieldError("country");
  const topBarActions = useCreatorTopBarActions({ saveHref: creatorListingsPath() });

  return (
    <>
      <Head title={t("web.services.creator.steps.selling_country.show.meta_title")}>
        <meta name="description" content={t("web.services.creator.steps.selling_country.show.meta_description")} />
      </Head>
      <ServiceSellingCountryStepView
        currentStage={currentStage}
        totalStages={totalStages}
        supportedCountries={supported_countries}
        value={form.data.country}
        error={countryError}
        currentCurrency={current_currency}
        repriceableCount={repriceable_count}
        confirmOpen={confirmOpen}
        primaryPending={primaryPending}
        topBarActions={topBarActions}
        onBack={onBack}
        onPrimary={handleContinue}
        onChange={(value) => form.setData("country", value)}
        onConfirmOpenChange={handleConfirmOpenChange}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default ServiceSellingCountryStep;
